import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiInfo, FiTrendingUp, FiShield, FiPercent, FiArrowRight, FiCheckCircle } = FiIcons;

const HowItWorksPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const methodology = [
    {
      title: "Contribution Split",
      desc: "Based on the standard statutory rates: 11% from the Employee's salary and 13% from the Employer's side (for salaries below RM5,000).",
      icon: FiPercent,
      color: "text-blue-600",
      bg: "bg-blue-50"
    },
    {
      title: "Compound Dividend",
      desc: "Our algorithm applies dividends annually, compounded over your entire career path until the target retirement age.",
      icon: FiTrendingUp,
      color: "text-emerald-600",
      bg: "bg-emerald-50"
    },
    {
      title: "Account Allocation",
      desc: "Calculation accounts for the 70/30 split between Account 1 (Retirement) and Account 2 (Discretionary/Housing).",
      icon: FiShield,
      color: "text-brand-600",
      bg: "bg-brand-50"
    }
  ];

  const dividends = [
    { year: 2023, rate: "5.50%", type: "Konvensional" },
    { year: 2022, rate: "5.35%", type: "Konvensional" },
    { year: 2021, rate: "6.10%", type: "Konvensional" },
    { year: 2020, rate: "5.20%", type: "Konvensional" },
    { year: 2019, rate: "5.45%", type: "Konvensional" },
  ];

  return (
    <div className="pt-32 pb-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="max-w-3xl mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-5xl font-black text-slate-900 tracking-tight mb-6">
              Our Methodology & <br />
              <span className="text-brand-600">How We Calculate</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Transparency is key to financial planning. We use official statutory rates and historical dividend data to provide the most accurate projections possible for your retirement.
            </p>
          </motion.div>
        </div>

        {/* Core Logic Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
          {methodology.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-8 rounded-[2.5rem] bg-slate-50 border border-slate-100 hover:shadow-xl hover:shadow-slate-200/50 transition-all"
            >
              <div className={`w-14 h-14 ${item.bg} ${item.color} rounded-2xl flex items-center justify-center mb-6`}>
                <SafeIcon icon={item.icon} className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Detailed Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-8">Historical Dividend Reference</h2>
            <div className="overflow-hidden rounded-3xl border border-slate-100 shadow-sm">
              <table className="w-full text-left">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4 text-sm font-bold text-slate-500 uppercase">Year</th>
                    <th className="px-6 py-4 text-sm font-bold text-slate-500 uppercase">Dividend Rate</th>
                    <th className="px-6 py-4 text-sm font-bold text-slate-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {dividends.map((d) => (
                    <tr key={d.year} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 font-bold text-slate-900">{d.year}</td>
                      <td className="px-6 py-4 text-brand-600 font-bold">{d.rate}</td>
                      <td className="px-6 py-4 text-slate-500 text-sm">{d.type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-slate-400 italic">
              * Source: Official Kumpulan Wang Simpanan Pekerja (KWSP) historical archives.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-3xl font-bold text-slate-900">Key Assumptions</h2>
            <div className="space-y-6">
              {[
                "Static salary growth (unless otherwise specified in advanced settings).",
                "Dividend rate remains consistent with the 5.5% long-term average.",
                "Statutory contribution rates remain at 11% (Employee) and 13% (Employer).",
                "Calculations assume no premature withdrawals from Account 1 or 2."
              ].map((text, i) => (
                <div key={i} className="flex gap-4">
                  <div className="mt-1">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-emerald-500" />
                  </div>
                  <p className="text-slate-600 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
            
            <div className="p-6 bg-brand-50 rounded-2xl border border-brand-100 flex gap-4">
              <SafeIcon icon={FiInfo} className="w-6 h-6 text-brand-600 shrink-0" />
              <p className="text-sm text-brand-800 leading-relaxed font-medium">
                Our tool is intended for estimation purposes only. For official statements, please log in to your i-Akaun on the KWSP website.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-slate-900 rounded-[3rem] p-12 text-center relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white mb-6">Ready to see your projection?</h2>
            <a 
              href="/#calculator" 
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand-600 text-white font-bold rounded-2xl hover:bg-brand-500 transition-all shadow-xl shadow-brand-600/20"
            >
              Go to Calculator
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </a>
          </div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-500/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorksPage;